import { useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import type { Coding } from '@medplum/fhirtypes'
import { buildProcedureMeta } from '@/utils/terminologies'
import { logError } from '@/utils/errorUtils'

export type ProcedureOption = {
  value: string
  label: string
  code: string
  display: string
  coding: Coding
  // Org-scoped base cash price from the Terminology tab, when set.
  price?: number
}

const toCodingKey = (coding: Coding): string => `${coding.system || ''}|${coding.code || ''}`

/**
 * Phase 5 item 5.4 — loads the practitioner's organization-scoped
 * CPT value set. Mirrors `BillingModal.tsx:90-150` but exposes only
 * the option list (Phase 5's `+ Add Order` is draft-only — no price
 * propagation, no FHIR resource creation).
 *
 * Failure mode: any error is logged via `logError` and the hook
 * returns an empty options array so the picker still mounts but
 * shows "no procedures found". This matches `BillingModal`'s
 * behavior in the absence of an organization-scoped code system.
 */
export function useProcedureOptions(): {
  options: ProcedureOption[]
  isLoading: boolean
} {
  const medplum = useMedplum()
  const [options, setOptions] = useState<ProcedureOption[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isActive = true

    const load = async () => {
      try {
        const profile = medplum.getProfile()
        if (!profile?.id) {
          return
        }

        const role = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        const orgRef = role?.organization?.reference
        const orgId = orgRef?.split('/').pop()
        if (!orgId || !isActive) {
          return
        }

        const { name, codeSystemUrl } = buildProcedureMeta(orgId)
        const codeSystem = await medplum.searchOne('CodeSystem', `name=${name}`)
        if (!isActive || !codeSystem?.concept?.length) {
          return
        }

        const next: ProcedureOption[] = []
        for (const concept of codeSystem.concept) {
          const typedConcept = concept
          if (!typedConcept.code) {
            continue
          }

          const coding: Coding = {
            system: codeSystem.url || codeSystemUrl,
            code: typedConcept.code,
            display: typedConcept.display || typedConcept.code,
          }

          const price = typedConcept.property?.find((p) => p.code === 'price')?.valueDecimal

          next.push({
            value: toCodingKey(coding),
            label: `${coding.code} - ${coding.display}`,
            code: coding.code || '',
            display: coding.display || '',
            coding,
            price,
          })
        }

        if (isActive) {
          setOptions(next)
        }
      } catch (err) {
        logError('patient', 'Failed to load procedure value set for ProgressNoteEdit', err)
      } finally {
        if (isActive) {
          setIsLoading(false)
        }
      }
    }

    load()

    return () => {
      isActive = false
    }
  }, [medplum])

  return { options, isLoading }
}
